import {
    FavoriteSection,
    SectionData,
    SectionType,
    SectionTypeConfig,
    SectionVariant,
} from '@superfunnel/types';
import {
    addDoc,
    arrayUnion,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    query,
    setDoc,
    updateDoc,
    where,
} from 'firebase/firestore';
import { uid } from 'uid';
import { db } from '../Firebase';
import { emptyConverter } from '../utils';

export const createFavoriteSection = async (
  accountId: string,
  dynamicPageId: string | null,
  name: string,
  section: Partial<SectionData>,
  createdBy: string,
): Promise<{ ok: boolean }> => {
  const favoriteSectionsColRef = collection(
    db,
    'accounts',
    accountId,
    'favoriteSections',
  ).withConverter(emptyConverter<FavoriteSection>());

  const docRef = doc(favoriteSectionsColRef);

  await setDoc(docRef, {
    campaignId: dynamicPageId,
    section,
    name,
    createdBy,
    createdAt: new Date(),
  });

  return { ok: true };
};

export const deleteFavoriteSection = async (
  accountId: string,
  favoriteSectionId: string,
): Promise<{ ok: boolean }> => {
  const favoriteSectionsColRef = collection(
    db,
    'accounts',
    accountId,
    'favoriteSections',
  ).withConverter(emptyConverter<FavoriteSection>());

  const docRef = doc(favoriteSectionsColRef, favoriteSectionId);

  await deleteDoc(docRef);

  return { ok: true };
};

export const addSectionToGlobalCatalog = async (
  sectionType: SectionType,
  section: SectionVariant,
) => {
  const globalSectionTypeConfigs = await getDocs(
    query(
      collection(db, 'sectionTypes').withConverter(
        emptyConverter<SectionTypeConfig>(),
      ),
      where('type', '==', sectionType),
      where('accountId', '==', ''),
    ),
  );

  const globalSectionTypeConfig = globalSectionTypeConfigs.docs[0];

  if (!globalSectionTypeConfig) {
    throw new Error('Section type not found');
  }

  await updateDoc(globalSectionTypeConfig.ref, {
    variants: arrayUnion({ ...section, id: uid(12) }),
  });
};

export const addSectionToAccountCatalog = async (
  accountId: string,
  sectionType: SectionType,
  section: SectionVariant,
) => {
  const sectionTypeConfigsColRef = collection(db, 'sectionTypes').withConverter(
    emptyConverter<SectionTypeConfig>(),
  );
  const sectionTypeConfigs = await getDocs(
    query(
      sectionTypeConfigsColRef,
      where('type', '==', sectionType),
      where('accountId', 'in', [accountId, '']),
    ),
  );

  const globalSectionTypeConfig = sectionTypeConfigs.docs.find(
    (d) => d.data().accountId === '',
  );

  const accountSectionTypeConfig = sectionTypeConfigs.docs.find(
    (d) => d.data().accountId === accountId,
  );

  if (accountSectionTypeConfig) {
    await updateDoc(accountSectionTypeConfig.ref, {
      variants: arrayUnion({ ...section, id: uid(12) }),
    });
  } else if (globalSectionTypeConfig) {
    await addDoc(sectionTypeConfigsColRef, {
      ...globalSectionTypeConfig.data(),
      variants: [{ ...section, id: uid(12) }],
      accountId: accountId,
    });
  } else {
    throw new Error('Section type not found');
  }
};

export const removeSectionVariantFromCatalog = async (
  sectionTypeConfigId: string,
  variantId: string,
) => {
  const sectionTypeConfigsColRef = collection(db, 'sectionTypes').withConverter(
    emptyConverter<SectionTypeConfig>(),
  );
  const sectionTypeConfig = await getDoc(
    doc(sectionTypeConfigsColRef, sectionTypeConfigId),
  );

  if (!sectionTypeConfig.exists()) {
    throw new Error('Section type not found');
  }

  await updateDoc(sectionTypeConfig.ref, {
    variants: sectionTypeConfig
      .data()
      .variants.filter((v: SectionVariant) => v.id !== variantId) as any,
  });
};
